<template>
  <div class="basic-diff-container">
    <el-table
      :row-key="rowKey"
      :data="rawData"
      :highlight-current-row="hightlight"
      ref="table"
      empty-text="Nothing changed!"
      border
      :stripe="stripe"
      :row-class-name="tableRowClassName"
      :header-row-style="{ height: headerHeight + 'px' }"
      :row-style="{ height: rowHeight + 'px' }"
      :expand-row-keys="expands"
      @row-click="selectOneRow"
    >
      <el-table-column type="expand" v-if="hasRowExpand">
        <template slot-scope="scope">
          <slot
            name="row-expand"
            v-bind:row="scope.row"
            v-bind:index="getRowIndex(scope.$index)"
          />
        </template>
      </el-table-column>
      <el-table-column label="What Changed" prop="label"> </el-table-column>
      <el-table-column
        v-for="(header, index) in tableHeaders"
        :key="'tabel-header-' + index"
        :prop="header.key"
        :label="header.label"
        :min-width="header.width"
      >
        <template slot-scope="scope">
          <el-tag
            class="basic-table_tag"
            size="small"
            v-if="get(scope.row, header.key).type === T.Tag"
            :effect="get(scope.row, header.key).effect"
          >
            {{ get(scope.row, header.key).value }}
          </el-tag>
          <div
            class="basic-table_timestamp f-c"
            v-else-if="get(scope.row, header.key).type === T.Time"
          >
            <span> {{ T.TimeSimple(get(scope.row, header.key).value) }} </span>
          </div>
          <el-progress
            :text-inside="true"
            :stroke-width="26"
            :percentage="get(scope.row, header.key).value"
            v-else-if="get(scope.row, header.key).type === T.Progress"
          />
          <address-tag
            :address="get(scope.row, header.key).value"
            v-else-if="get(scope.row, header.key).type === T.AddressTag"
          />
          <hold-or-not
            :value="get(scope.row, header.key).value"
            v-else-if="get(scope.row, header.key).type === T.HoldTag"
          />
          <el-checkbox
            v-else-if="get(scope.row, header.key).type === T.Checkbox"
            :value="get(scope.row, header.key).value"
          />
          <div class="basic-table_common-text" v-else>
            {{ get(scope.row, header.key).value }}
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>
<script>
// others
import get from 'lodash/get'
// types
import * as T from '@/core/configs/types'
import { pageJumps } from '@/core/mixin/pages'
import AddressTag from '@/components/address/AddressTag.vue'
import HoldOrNot from './HoldOrNot.vue'
export default {
  name: 'BasicDiff',
  mixins: [pageJumps],
  components: { AddressTag, HoldOrNot },
  props: {
    tableData: {
      type: Object,
      default: () => null
    },
    hasRowExpand: {
      type: Boolean,
      default: false
    },
    handleSelectionChange: {
      type: Function,
      default: () => {}
    },
    rowClassName: {
      type: Function,
      default: () => {}
    },
    hightlight: {
      type: Boolean,
      default: false
    },
    checkExpand: {
      type: Function,
      default: () => false
    },
    headerHeight: {
      type: Number,
      default: 50
    },
    rowHeight: {
      type: Number,
      default: 60
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    stripe: {
      ttype: Boolean,
      default: true
    }
  },
  data() {
    return {
      T,
      columnToType: {},
      tableHeaders: [
        {
          key: 'oldValue',
          label: 'Old Value'
        },
        {
          key: 'newValue',
          label: 'New Value'
        }
      ]
    }
  },
  computed: {
    rawData() {
      return this.tableData?.records ?? []
    },
    total() {
      return this.tableData?.total ?? 0
    },
    curPage() {
      return this.tableData?.current ?? 1
    },
    pageSize() {
      return this.tableData?.size ?? 20
    },
    expands() {
      return this.rawData
        .filter(one => this.checkExpand(one))
        .map(one => get(one, this.rowKey))
    }
  },
  methods: {
    get,
    getRowIndex(index) {
      return (this.curPage - 1) * this.pageSize + index
    },
    tableRowClassName({ row, rowIndex }) {
      return this.rowClassName(row, rowIndex)
    },
    setCurrentRow(row) {
      this.$refs.table.setCurrentRow(row)
    },
    selectOneRow(row) {
      this.$emit('selectedRow', row)
    },
    getTagType(header, val) {
      switch (typeof header.tagType) {
        case 'string':
          return header.tagType
        case 'function':
          return header.tagType(val)
        default:
          return null
      }
    },
    linkClick(methodName, params) {
      this[methodName](params)
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.el-table ::v-deep th {
  font-size: $title-level-regular !important;
  padding: 5px 0 !important;

  .cell {
    padding: 0 5px !important;
    text-align: center;
  }
}

.el-table ::v-deep td {
  padding: 8px 0 !important;

  .cell {
    padding: 0 5px !important;

    @extend .f-r-c-c;
  }
}

.el-table ::v-deep.error-row {
  background-color: $table-row-danger-color !important;
}

.el-table ::v-deep.success-row {
  background-color: $table-row-success-color !important;
}

.el-table ::v-deep.el-table__fixed-right {
  height: 100% !important;
}

.el-table ::v-deep .table-href {
  text-decoration: underline;
  color: $business-color;

  &:hover {
    cursor: pointer;
    // color: rgba($el-primary-color, 1);
  }
}

.basic-table_timestamp {
  font-size: $regular-body-font-size;
  text-align: center;
}

.table-container {
  border-radius: 3px;

  .basic-table-footer {
    margin-top: 10px;
    width: 100%;
    justify-content: flex-end;
    gap: 20px;

    @extend .f-r-vc;
    @extend .f-r-hc;

    .pagination {
      ::v-deep .el-pagination {
        .el-pagination__sizes {
          .el-select {
            .el-input {
              .el-input__inner {
                border-top: none !important;
                border-left: none !important;
                border-right: none !important;
                border-radius: 0 !important;
              }
            }
          }
        }
      }

      .el-button {
        border: none;
      }
    }
  }
}

.el-progress {
  width: 90%;
}

::v-deep .el-table__body tr.current-row > td {
  background-color: $table-current-row-color !important;
}

.el-table ::v-deep .el-table__expand-icon {
  width: 32px;
  height: 32px;
}
</style>
